import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type UserRole = "admin" | "hr_manager" | "hr_clerk" | "employee" | null;
type Profile = { full_name: string; employee_id?: string } | null;

type AuthContextValue = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  profile: Profile;
  userRole: UserRole;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<Profile>(null);
  const [userRole, setUserRole] = useState<UserRole>(null);

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, employee_id")
        .eq("id", userId)
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  }, []);

  const fetchUserRole = useCallback(async (userId: string) => {
    try {
      // Use RPC to get highest priority role (handles users with multiple roles)
      const { data, error } = await supabase
        .rpc("get_primary_role", { p_user_id: userId });

      if (error) throw error;
      setUserRole((data as UserRole) || null);
    } catch (error) {
      console.error("Error fetching user role:", error);
      setUserRole(null);
    }
  }, []);

  useEffect(() => {
    // Listen for auth changes (single global source of truth)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        setTimeout(() => {
          fetchProfile(session.user.id);
          fetchUserRole(session.user.id);
        }, 0);
      } else {
        setProfile(null);
        setUserRole(null);
      }

      if (event === "SIGNED_OUT") {
        setLoading(false);
      }
    });

    // Initialize from persisted session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        fetchProfile(session.user.id);
        fetchUserRole(session.user.id);
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile, fetchUserRole]);

  const signOut = useCallback(async () => {
    // Optimistic UI: immediately drop auth state so ProtectedRoute can react.
    setSession(null);
    setUser(null);
    setProfile(null);
    setUserRole(null);

    // Best-effort server revoke (may return session_not_found for stale JWTs)
    try {
      await supabase.auth.signOut({ scope: "global" });
    } catch {
      // ignore
    }

    // Always clear local session so /auth doesn't instantly bounce back.
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, session, loading, profile, userRole, signOut }),
    [user, session, loading, profile, userRole, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
};

